// ── PROJECT CONTEXT ──
// Per MULTI-ROLE-DESIGN.md. Holds the user's project memberships and
// which project they're currently viewing. Components read `activeProject`
// and the derived role booleans (isCaptain, isStoryteller, isCrew) instead
// of the legacy global profile.role.
//
// Loaded on auth via GET /api/me/projects. Switching persists to backend
// (POST /api/me/active-project) and to localStorage as a fast-startup
// hint for the next page load.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext.jsx';
import { me } from '@/services/api.js';

const ProjectContext = createContext(null);

const LS_KEY = 'doihaveastory.activeProjectId';

export function ProjectProvider({ children }) {
  const { user, loading: authLoading } = useAuth();

  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!user) {
      // Clear all project state on sign-out so the next user doesn't see
      // a flash of stale data.
      setProjects([]);
      setActiveProjectId(null);
      setLoading(false);
      return;
    }
    try {
      const res = await me.projects();
      const list = (res && res.projects) || [];
      setProjects(list);
      // Choose the active project:
      // 1. If localStorage hint is still a valid member → keep it.
      // 2. Else if the server says lastActive is valid → use that.
      // 3. Else if exactly one project exists → that one.
      // 4. Else → null (user gets the picker).
      setActiveProjectId(prev => {
        if (prev && list.some(p => p.storytellerProfileId === prev)) return prev;
        const serverLast = res && res.lastActiveStorytellerProfileId;
        if (serverLast && list.some(p => p.storytellerProfileId === serverLast)) return serverLast;
        if (list.length === 1) return list[0].storytellerProfileId;
        return null;
      });
      setError(null);
    } catch (err) {
      console.warn('[ProjectContext] /projects failed:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // (Re)load projects whenever auth changes.
  useEffect(() => {
    if (authLoading) return;
    setLoading(true);
    refresh();
  }, [authLoading, user?.id, refresh]);

  // Mirror activeProjectId to localStorage so a refresh feels instant.
  useEffect(() => {
    try {
      if (activeProjectId) localStorage.setItem(LS_KEY, activeProjectId);
      else localStorage.removeItem(LS_KEY);
    } catch {}
  }, [activeProjectId]);

  // Public: switch to a different project. Awaits the backend write before
  // returning, because callers reload the page right after — and the
  // legacy useStorytellers hook prefers profile.active_storyteller_id from
  // the database over localStorage, so a stale DB value would override the
  // fresh localStorage value and show the wrong storyteller.
  const switchProject = useCallback(async (storytellerProfileId) => {
    if (!storytellerProfileId) return;
    if (!projects.some(p => p.storytellerProfileId === storytellerProfileId)) {
      throw new Error('You are not a member of that project');
    }
    setActiveProjectId(storytellerProfileId);
    try {
      await me.setActiveProject(storytellerProfileId);
    } catch (err) {
      console.warn('[ProjectContext] setActiveProject failed:', err.message);
    }
  }, [projects]);

  const activeProject = useMemo(
    () => projects.find(p => p.storytellerProfileId === activeProjectId) || null,
    [projects, activeProjectId]
  );

  // Convenience role booleans derived from the ACTIVE project's role. Use
  // these instead of profile.role for any in-app gating.
  const value = useMemo(() => ({
    projects,
    activeProject,
    activeProjectId,
    loading,
    error,
    switchProject,
    refresh,
    // Per-project role booleans
    role:          activeProject ? activeProject.role : null,
    isCaptain:     activeProject ? activeProject.role === 'captain'     : false,
    isStoryteller: activeProject ? activeProject.role === 'storyteller' : false,
    isCrew:        activeProject ? activeProject.role === 'crew'        : false,
    // True if the user has at least one project but hasn't selected one yet.
    needsPicker:   !activeProject && projects.length > 1,
    hasNoProjects: !loading && projects.length === 0,
  }), [projects, activeProject, activeProjectId, loading, error, switchProject, refresh]);

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProject() {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProject must be used inside ProjectProvider');
  return ctx;
}
